import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Pencil, Plus, Settings, Volleyball, Zap } from 'lucide-react';

import {
  Button,
  CellStack,
  Column,
  DataTable,
  EmptyState,
  FilterTabs,
  PageHeader,
  StatusBadge,
  Text,
  Toolbar,
} from '@/components/flup';

import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import {
  checkAgreementStatus,
  getCompletedVenueDetails,
  getPendingVenueDetails,
} from '@/store/thunkMidddleware/turfMiddleware';
import { setCurrentOnboardVenueDetails } from '@/store/slices/turfSlice';
import Payment from '@/pages/Payment/Payment';
import GoLiveModal, { PlanSelection } from '@/components/turf/GoLiveModal';
import VendorAgreementModal from '@/components/turf/VendorAgreementModal';
import PaymentSuccessAnimation from '@/components/animation/PaymentSuccessAnimation';
import routes from '@/routes/routes.json';

/**
 * Every venue the vendor owns, and the one action each is waiting on.
 *
 * A venue moves through a fixed pipeline — set up → pay the onboarding fee →
 * get verified → live — and the only thing a vendor wants from this screen is
 * "what do I do next about this one". So the table's last column is a single
 * button that changes with the stage, rather than a row of buttons where all
 * but one are inert. `nextAction` below is the whole state machine, in one place.
 */

type Tab = 'Completed' | 'Pending';

interface Venue {
  _id: string;
  name: string;
  venueThumbnail?: string;
  venueOnBoardingPercentage?: number;
  isVerified?: boolean;
  oneTimeOnboardingFeeDetails?: { isPaymentdone?: boolean };
  address?: { city?: string };
  sports?: { courts?: unknown[] }[];
}

/** The venue's stage, and the single thing to do about it. */
const stageOf = (venue: Venue) => {
  const setupDone = Math.round(venue?.venueOnBoardingPercentage ?? 0) === 100;
  const paid = Boolean(venue?.oneTimeOnboardingFeeDetails?.isPaymentdone);

  if (!setupDone) return 'setup' as const;
  if (!paid) return 'unpaid' as const;
  if (!venue?.isVerified) return 'review' as const;
  return 'live' as const;
};

const STAGE_STATUS: Record<ReturnType<typeof stageOf>, string> = {
  setup: 'draft',
  unpaid: 'pending',
  review: 'unverified',
  live: 'live',
};

const STAGE_LABEL: Record<ReturnType<typeof stageOf>, string> = {
  setup: 'Setup incomplete',
  unpaid: 'Payment pending',
  review: 'In verification',
  live: 'Live',
};

const VenueList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { pendingVenueDetails, completedVenueDetails, checkAgreementStatusResponse } =
    useAppSelector((state: RootState) => state.turf);

  const [currentTab, setCurrentTab] = useState<Tab>('Completed');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [search, setSearch] = useState('');

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [venueIdForPayment, setVenueIdForPayment] = useState<string | null>(null);
  const [venueNameForPayment, setVenueNameForPayment] = useState<string | null>(null);
  // The plan the vendor picked in the go-live modal, sent with the order so the
  // backend can attach the listing fee to a subscription's first debit.
  const [planForPayment, setPlanForPayment] = useState<PlanSelection | null>(null);
  const [initRazorpayPayment, setInitRazorpayPayment] = useState(false);
  const [showPaymentCongratulationModal, setShowPaymentCongratulationModal] = useState(false);

  const [showAgreementModal, setShowAgreementModal] = useState(false);

  /* ------------------------------------------------------------------ data */

  const fetchVenues = (tab: Tab) => {
    const thunk = tab === 'Pending' ? getPendingVenueDetails : getCompletedVenueDetails;
    dispatch(
      thunk({
        callback: (res: any) => setVenues(res?.payload?.length ? res.payload : []),
      }),
    );
  };

  useEffect(() => {
    fetchVenues(currentTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return venues;
    return venues.filter((v) => String(v?.name || '').toLowerCase().includes(q));
  }, [venues, search]);

  /* --------------------------------------------------------------- actions */

  const editHandler = (venue: Venue) => {
    dispatch(setCurrentOnboardVenueDetails(venue));
    navigate(`${routes.editVenue}/${venue?._id}`);
  };

  const manageVenueHandler = (venue: Venue) =>
    navigate(`${routes.venueManagement}/${venue?._id}`);

  const goLiveHandler = (venue: Venue) => {
    setVenueIdForPayment(venue?._id);
    setVenueNameForPayment(venue?.name ?? null);

    dispatch(
      checkAgreementStatus({
        data: { venueId: venue?._id },
        callback: (res: any) => {
          if (res?.payload?.isSigned && res?.payload?.agreement) setShowPaymentModal(true);
          else setShowAgreementModal(true);
        },
      }),
    );
  };

  /* --------------------------------------------------------------- columns */

  const columns: Column<Venue>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Venue',
        width: 'min-w-[220px]',
        render: (venue) => (
          <div className="flex items-center gap-3 min-w-0">
            {venue?.venueThumbnail ? (
              <img
                src={venue.venueThumbnail}
                alt=""
                className="w-9 h-9 rounded-flup-xs object-cover shrink-0 bg-flup-sunken"
              />
            ) : (
              <span className="grid place-items-center w-9 h-9 rounded-flup-xs bg-flup-sunken text-flup-ink-3 shrink-0">
                <Building2 className="w-4 h-4" aria-hidden />
              </span>
            )}
            <CellStack primary={venue?.name} secondary={venue?.address?.city} />
          </div>
        ),
      },
      {
        key: 'stage',
        header: 'Status',
        render: (venue) => {
          const stage = stageOf(venue);
          return <StatusBadge status={STAGE_STATUS[stage]} label={STAGE_LABEL[stage]} />;
        },
      },
      {
        key: 'progress',
        header: 'Setup',
        hideBelow: 'md',
        width: 'w-40',
        render: (venue) => {
          const pct = Math.round(venue?.venueOnBoardingPercentage ?? 0);
          return (
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex-1 h-1.5 rounded-pill bg-flup-sunken overflow-hidden min-w-[60px]">
                <span
                  style={{ width: `${pct}%` }}
                  className={cnProgress(pct)}
                />
              </div>
              <Text variant="micro" tone="tertiary" as="span" className="nums shrink-0">
                {pct}%
              </Text>
            </div>
          );
        },
      },
      {
        key: 'courts',
        header: 'Courts',
        align: 'right',
        hideBelow: 'lg',
        render: (venue) => (
          <Text variant="bodyMedium" as="span" className="nums">
            {(venue?.sports || []).reduce((sum, s) => sum + (s?.courts?.length || 0), 0)}
          </Text>
        ),
      },
      {
        key: 'action',
        header: '',
        align: 'right',
        width: 'w-[160px]',
        render: (venue) => <NextAction venue={venue} />,
      },
    ],
    // The action column closes over handlers that are stable for a render pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkAgreementStatusResponse.isLoading],
  );

  /** The single thing to do about this venue, given its stage. */
  const NextAction: React.FC<{ venue: Venue }> = ({ venue }) => {
    const stage = stageOf(venue);

    if (stage === 'setup') {
      return (
        <Button
          size="sm"
          variant="secondary"
          icon={<Pencil className="w-3.5 h-3.5" />}
          onClick={() => editHandler(venue)}
        >
          Finish setup
        </Button>
      );
    }

    if (stage === 'unpaid') {
      return (
        <Button
          size="sm"
          icon={<Zap className="w-3.5 h-3.5" />}
          loading={checkAgreementStatusResponse.isLoading}
          onClick={() => goLiveHandler(venue)}
        >
          Go live
        </Button>
      );
    }

    if (stage === 'review') {
      return (
        <Text variant="micro" tone="tertiary" as="span">
          We'll call within 24h
        </Text>
      );
    }

    return (
      <div className="flex items-center justify-end gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          icon={<Settings className="w-3.5 h-3.5" />}
          onClick={() => editHandler(venue)}
        >
          Settings
        </Button>
        <Button
          size="sm"
          icon={<Volleyball className="w-3.5 h-3.5" />}
          onClick={() => manageVenueHandler(venue)}
        >
          Slots
        </Button>
      </div>
    );
  };

  const loading = pendingVenueDetails.isLoading || completedVenueDetails.isLoading;

  return (
    <div className="space-y-4">
      {showPaymentCongratulationModal ? (
        <PaymentSuccessAnimation
          show={showPaymentCongratulationModal}
          venueName={venueNameForPayment ?? undefined}
          planLabel={
            planForPayment?.plan === 'SUBSCRIPTION'
              ? `Zero commission · ${planForPayment.billingCycle}`
              : planForPayment
                ? 'Pay per booking'
                : undefined
          }
          action={() => {
            setShowPaymentCongratulationModal(false);
            setVenueNameForPayment(null);
            setPlanForPayment(null);
          }}
        />
      ) : null}

      {initRazorpayPayment && venueIdForPayment ? (
        <Payment
          venueId={venueIdForPayment}
          plan={planForPayment?.plan ?? null}
          billingCycle={planForPayment?.billingCycle ?? null}
          PaymentCloseHandler={() => {
            setVenueIdForPayment(null);
            setInitRazorpayPayment(false);
          }}
          PaymentSuccesshandler={() => {
            setVenueIdForPayment(null);
            setInitRazorpayPayment(false);
            setShowPaymentModal(false);
            fetchVenues(currentTab);
            setShowPaymentCongratulationModal(true);
          }}
        />
      ) : null}

      <PageHeader
        title="Venues"
        count={filtered.length ? `${filtered.length} venues` : undefined}
        subtitle="Everything you run, and what each one is waiting on."
        actions={
          <Button icon={<Plus className="w-4 h-4" />} onClick={() => navigate(routes.venueOnboard)}>
            Add a venue
          </Button>
        }
      />

      <FilterTabs
        items={[
          { key: 'Completed', label: 'Set up' },
          { key: 'Pending', label: 'In progress' },
        ]}
        value={currentTab}
        onChange={(key) => setCurrentTab(key as Tab)}
      />

      <Toolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search venues" />

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(v) => String(v._id)}
        loading={loading}
        empty={
          <EmptyState
            icon={<Building2 />}
            title={search ? 'No venue matches that search' : 'No venues here yet'}
            message={
              search
                ? 'Try a different name.'
                : currentTab === 'Completed'
                  ? 'Venues you have finished setting up will show up here.'
                  : 'Venues still being set up will show up here.'
            }
            actionLabel={search ? undefined : 'Add a venue'}
            onAction={search ? undefined : () => navigate(routes.venueOnboard)}
          />
        }
      />

      {/* Agreement */}
      <VendorAgreementModal
        open={showAgreementModal}
        onOpenChange={setShowAgreementModal}
        venueId={venueIdForPayment}
        onSigned={() => setShowPaymentModal(true)}
      />

      {/* Go live: plan choice + the one-time listing fee */}
      <GoLiveModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        venueId={venueIdForPayment}
        venueName={venueNameForPayment ?? undefined}
        onPay={(selection) => {
          setPlanForPayment(selection);
          setShowPaymentModal(false);
          setInitRazorpayPayment(true);
        }}
      />
    </div>
  );
};

/** Progress fill: brand teal while in flight, green once the setup is done. */
const cnProgress = (pct: number) =>
  `block h-full rounded-pill transition-[width] duration-500 ${
    pct >= 100 ? 'bg-flup-green' : 'bg-flup-brand'
  }`;

export default VenueList;
